// src/context/AuthContext.tsx - ENHANCED: Store user data and fetch initial balance
import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { User } from '../types';
import { API_BASE_URL } from '../constants';

interface AuthContextType {
  apiKey: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (apiKey: string, userData?: User) => Promise<boolean>;
  logout: () => void;
  validateApiKey: (apiKey: string) => Promise<boolean>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'ppl_contact_api_key';
const USER_DATA_KEY = 'auth_user_data';

// ENHANCED: Validation with user info fetching
const validateApiKeyWithRetry = async (key: string, maxRetries = 2): Promise<{ isValid: boolean; user?: User }> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (!key.trim()) {
        return { isValid: false };
      }

      console.log(`🔍 Validating API key (attempt ${attempt + 1}/${maxRetries + 1})...`);
      
      // Try schema endpoint first
      const schemaResponse = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://api.ppl.contact'}/api/schema`, 
        {
          headers: {
            'Authorization': `Bearer ${key}`,
            'Content-Type': 'application/json'
          },
          signal: AbortSignal.timeout(10000)
        }
      );

      if (schemaResponse.ok) {
        const data = await schemaResponse.json();
        if (data.success === true) {
          // Try to fetch user info
          try {
            const userResponse = await fetch(`${API_BASE_URL}/api/users/me`, {
              headers: {
                'Authorization': `Bearer ${key}`,
                'Content-Type': 'application/json'
              },
              signal: AbortSignal.timeout(5000)
            });
            
            if (userResponse.ok) {
              const user = await userResponse.json();
              // Store user data for billing initialization
              localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
              return { isValid: true, user };
            }
          } catch {
            console.log('ℹ️ User info endpoint not available, continuing without user data');
          }
          
          return { isValid: true };
        }
      }

      // Fallback to health endpoint
      if (schemaResponse.status === 403) {
        console.log('🔍 Schema requires special permissions, trying health endpoint...');
        const healthResponse = await fetch(`${API_BASE_URL}/health`, {
          headers: {
            'Authorization': `Bearer ${key}`,
            'Content-Type': 'application/json'
          },
          signal: AbortSignal.timeout(10000)
        });

        if (healthResponse.ok) {
          return { isValid: true };
        }

        // Final fallback to root endpoint
        if (healthResponse.status === 403) {
          console.log('🔍 Health requires special permissions, trying root endpoint...');
          const rootResponse = await fetch(`${API_BASE_URL}/`, {
            headers: {
              'Authorization': `Bearer ${key}`,
              'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(10000)
          });

          if (rootResponse.ok) {
            return { isValid: true };
          }
        }
      }

      // If we get here and it's not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }

    } catch (error) {
      console.error(`❌ API key validation attempt ${attempt + 1} failed:`, error);
      
      // If it's a timeout or network error and not the last attempt, retry
      if (attempt < maxRetries && (
        error instanceof Error && (
          error.name === 'AbortError' || 
          error.message.includes('timeout') ||
          error.message.includes('network')
        )
      )) {
        console.log(`⏳ Retrying in ${(attempt + 1)}s...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        continue;
      }
    }
  }

  return { isValid: false };
};

interface AuthProviderProps {
  children: React.ReactNode;
  initialApiKey?: string | null;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children, initialApiKey }) => {
  const [apiKey, setApiKey] = useState<string | null>(initialApiKey || null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ENHANCED: Validation function with user info
  const validateApiKey = useCallback(async (key: string): Promise<boolean> => {
    const result = await validateApiKeyWithRetry(key);
    if (result.user) {
      setUser(result.user);
    }
    return result.isValid;
  }, []);

  // Load stored API key with enhanced user info handling
  useEffect(() => {
    const loadStoredApiKey = async () => {
      try {
        const storedKey = localStorage.getItem(AUTH_STORAGE_KEY);
        const storedUserData = localStorage.getItem(USER_DATA_KEY);
        
        // Try to restore user data first
        if (storedUserData) {
          try {
            const userData = JSON.parse(storedUserData);
            setUser(userData);
          } catch {
            localStorage.removeItem(USER_DATA_KEY);
          }
        }
        
        // Priority: initialApiKey (from Firebase auth) > stored key
        const keyToValidate = initialApiKey || storedKey;
        
        if (keyToValidate && keyToValidate !== 'null' && keyToValidate !== 'undefined') {
          console.log('🔑 Found API key, validating...');
          const result = await validateApiKeyWithRetry(keyToValidate);
                  if (result.isValid) {
          console.log(`🔧 AuthContext setApiKey from validation: ${keyToValidate ? 'present' : 'null'}`);
          setApiKey(keyToValidate);
          if (result.user) {
            setUser(result.user);
          }
            // Store the key if it came from Firebase auth
            if (initialApiKey && initialApiKey !== storedKey) {
              localStorage.setItem(AUTH_STORAGE_KEY, initialApiKey);
            }
            console.log('✅ API key validated successfully');
          } else {
            console.log('❌ API key is invalid, removing...');
            localStorage.removeItem(AUTH_STORAGE_KEY);
            localStorage.removeItem(USER_DATA_KEY);
            setApiKey(null);
            setUser(null);
          }
        } else {
          console.log('🔑 No valid API key found');
          setApiKey(null);
          setUser(null);
        }
      } catch (error) {
        console.error('❌ Error loading API key:', error);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(USER_DATA_KEY);
        setApiKey(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredApiKey();
  }, [initialApiKey]);

  // ENHANCED: Login function with user info and initial balance fetching
  const login = useCallback(async (key: string, userData?: User): Promise<boolean> => {
    if (!key || key.trim() === '' || key === 'null' || key === 'undefined') {
      console.warn('⚠️ Invalid API key provided to login');
      return false;
    }
    
    setIsLoading(true);
    try {
      // If userData is provided, use it directly
      if (userData) {
        console.log(`🔧 AuthContext setApiKey from login with user data: ${key ? 'present' : 'null'}`);
        setApiKey(key);
        setUser(userData);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        localStorage.setItem(AUTH_STORAGE_KEY, key);
        
        // One-time payment method check on sign-in
        try {
          const balanceResponse = await fetch(`${API_BASE_URL}/api/billing/balance`, {
            headers: {
              'Authorization': `Bearer ${key}`,
              'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(5000)
          });
          
          if (balanceResponse.ok) {
            const balanceData = await balanceResponse.json();
            console.log('💳 Payment method status:', balanceData.has_payment_method ? 'HAS_PAYMENT_METHOD' : 'NO_PAYMENT_METHOD');
          }
        } catch (error) {
          console.warn('⚠️ Failed to check payment method status:', error);
        }
        
        console.log('✅ API key authenticated and stored with user data');
        return true;
      }
      
      // Otherwise, validate the API key and fetch user data
      const result = await validateApiKeyWithRetry(key);
      if (result.isValid) {
        console.log(`🔧 AuthContext setApiKey from login: ${key ? 'present' : 'null'}`);
        setApiKey(key);
        if (result.user) {
          setUser(result.user);
          localStorage.setItem(USER_DATA_KEY, JSON.stringify(result.user));
          
          // Fetch initial balance for all authenticated users (production billing)
          try {
            const balanceResponse = await fetch(`${API_BASE_URL}/api/billing/balance`, {
              headers: {
                'Authorization': `Bearer ${key}`,
                'Content-Type': 'application/json'
              },
              signal: AbortSignal.timeout(5000)
            });
            
            if (balanceResponse.ok) {
              const balanceData = await balanceResponse.json();
              console.log('💳 Initial balance loaded:', balanceData);
              
              // One-time payment method check on sign-in
              console.log('💳 Payment method status:', balanceData.has_payment_method ? 'HAS_PAYMENT_METHOD' : 'NO_PAYMENT_METHOD');
              
              // Note: The balance will be managed by the billing hook in the app context
            }
          } catch (error) {
            console.warn('⚠️ Failed to fetch initial balance:', error);
            // Not a critical error, continue with login
          }
        }
        localStorage.setItem(AUTH_STORAGE_KEY, key);
        console.log('✅ API key authenticated and stored');
        return true;
      }
      console.log('❌ API key validation failed');
      return false;
    } catch (error) {
      console.error('❌ Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Enhanced logout function
  const logout = useCallback(() => {
    setApiKey(null);
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    console.log('🔓 Logged out and API key removed');
  }, []);

  // Permission checker
  const hasPermission = useCallback((permission: string): boolean => {
    return user?.permissions?.includes(permission) ?? false;
  }, [user]);

  // Memoized context value
  const value = useMemo<AuthContextType>(() => {
    console.log(`🔧 AuthContext value update - apiKey: ${apiKey ? 'present' : 'null'}, isAuthenticated: ${!!apiKey}`);
    return {
      apiKey,
      user,
      isAuthenticated: !!apiKey,
      isLoading,
      login,
      logout,
      validateApiKey,
      hasPermission
    };
  }, [apiKey, user, isLoading, login, logout, validateApiKey, hasPermission]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthProvider;